import React, { useState, Fragment, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Field } from "formik";
import FileUploadForm from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm";
import { getChangedDocumentsModuleData } from "config/changesDocumentData";
import _get from "lodash/get";
import Button from "components/lib/Shared/Button";
import { downloadBusinessDocumentsAsync } from "state/slices/company";
import { useToast } from "hooks/useToast";
import { handleFilesUpload } from "utils/filesUtils";
import { createUploadFilePayload } from "utils/transactionRequestHandler";
import { uploadTemplateFileAsync } from "state/slices/templateFile";
import { updateTransaction } from "state/slices/transaction";
import Checkbox from "components/lib/Shared/Checkbox";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import { Menu, Transition } from "@headlessui/react";
import { usePopper } from "hooks/usePopper";
import { ChevronDownIcon } from "@heroicons/react/outline";
import { Constants } from "config/constants";
import { addComment, editComment } from "state/slices/comment";
import CommentsList from "components/lib/Global/CommentsList";

/** Helper functions */
const getSignedApprovalDocs = (transactions, docName) => {
  const transaction = transactions.find((trx) =>
    trx?.fileUploads ? trx?.fileUploads[docName] : {}
  );
  return transaction?.fileUploads ? transaction?.fileUploads[docName] : null;
};

/**
 * ApprovalDocument component handles the display and logic for uploading, downloading,
 * and updating approval documents for various transaction types (director, shareholder, etc.).
 * It supports both unsigned and signed document flows, and toggling between e-signature and manual signing.
 */
const ApprovalDocument = ({
  selectedTab,
  document,
  documentType,
  signedDocumentType,
  isViewMode,
  fileUploads,
  setEsignatureIsSelected,
  esignatureIsSelected,
}) => {
  const dispatch = useDispatch();
  const { transactionUnderReview } = useSelector(
    (state) => state.transactionSlice
  );
  const { company } = useSelector((state) => state.companySlice);
  const { comments } = useSelector((state) => state.commentsSlice);
  const templateComments = comments.filter(
    (comment) => comment.templateFile === document.templateFile._id
  );
  const { approvalDocs, transactions } = transactionUnderReview.items;

  // Find the index and name of the current approval document
  const officialIndex = approvalDocs.findIndex(
    (doc) =>
      Object.values(doc)[0]?.templateFile._id === document?.templateFile?._id
  );
  const documentName = Object.keys(approvalDocs[officialIndex]);
  const [isDownloading, setIsDownloading] = useState(false);
  const { toast } = useToast();
  const isRegistrar = true; // TODO: Replace with actual registrar check if needed
  const [trigger, container] = usePopper({
    placement: "bottom-end",
    strategy: "fixed",
    modifiers: [{ name: "offset", options: { offset: [0, 10] } }],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [transactionStatus, setTransactionStatus] = useState("");

  let unsignedFiles = {};
  let signedFiles = {};
  if (document.templateFile?.modeType === "not-signed-documents") {
    unsignedFiles = {
      [documentType]: document.templateFile?.fileName ?? document?.convertedPdf,
    };
    signedFiles = {
      [documentType]: document?.signedPdf,
    };
  }


  if (document.templateFile?.modeType === "pre-signed-documents") {
    signedFiles = {
      [documentType]: document.templateFile.fileName,
    };
  }

  // Update transaction status when transactions change
  useEffect(() => {
    setTransactionStatus(transactions[0]?.status);
  }, [transactions]);

  const handleSaveComment = async (data) => {
    if (data?.commentId) {
      await dispatch(editComment({ commentId: data.commentId, data }));
    } else {
      await dispatch(
        addComment({
          data: {
            ...data,
            company: company._id,
            templateFile: document.templateFile._id,
          },
        })
      );
    }
  };
  return (
    <div className="py-4 mt-2">
      {/* Document selection and status */}
      <div>
        <h3 className="font-semibold text-slate-800">Document Selected:</h3>
        <div className="flex px-2 py-2 space-x-2">
          <Checkbox
            name="Signed Documents"
            label="Signed Documents"
            checked={document.templateFile?.modeType === "pre-signed-documents"}
            disabled
          />
          <Checkbox
            name="notSignedDocuments"
            label="Not Signed Documents"
            checked={document.templateFile?.modeType === "not-signed-documents"}
            disabled
          />
        </div>
      </div>
      {/* Unsigned version upload and download */}
      <div className="my-4 flex gap-5">
        {Object.keys(unsignedFiles).length > 0 && (
          <div className="w-full">
            <div className="px-2">Unsigned version</div>
            <div className="p-3 mt-2 bg-white">
              <Formik
                enableReinitialize
                initialValues={{
                  fileUploads: {
                    ...unsignedFiles,
                  },
                }}
                onSubmit={() => null}
              >
                {() => (
                  <div className="px-5">
                    <Field
                      name="fileUploads"
                      component={FileUploadForm}
                      documentData={getChangedDocumentsModuleData(documentType)}
                      resolvedFields={
                        document.resolvedFields ||
                        _get(document, "resolvedFields")
                      }
                      onlyPreview
                      showDocumentLabel
                      addReviewComment={!isViewMode}
                      hideResolveAllCheckbox={!isViewMode}
                      formName="approvalDocuments"
                      formHelperKey="ManagerCompanyDocument"
                      updateEffectKey={"updateTransactionUnderReview"}
                      updateEffectPath={`transactionSlice.transactionUnderReview.items.approvalDocs.${officialIndex}.${documentName}`}
                      formId={_get(document?.templateFile, "_id")}
                      resolveAllFieldsCheckbox
                      documentName={documentType}
                      comments={templateComments}
                      editorParams={{
                        editorKey: document?.templateFile?.fileName,
                      }}
                    />
                  </div>
                )}
              </Formik>
            </div>
          </div>
        )}

        {/* Signed version upload */}
        {(document.templateFile?.modeType === "pre-signed-documents" ||
          (document.templateFile?.modeType === "not-signed-documents" &&
            document?.signedPdf)) && (
            <div className="w-full">
              <div className="px-2">Signed version</div>
              <div className="p-3 mt-2 bg-white">
                <Formik
                  enableReinitialize
                  initialValues={{
                    fileUploads: {
                      ...signedFiles,
                    },
                  }}
                  onSubmit={() => {
                    /* No action needed on submit */
                  }}
                >
                  {() => (
                    <div className="pl-4">
                      {templateComments.length > 0 && (
                        <div className="px-8 pt-5 pb-6 mt-4 bg-white">
                          <CommentsList
                            comments={templateComments}
                            toggleCommentStatus={true}
                          />
                          <p className="mt-6 text-sm text-primary">
                            Comments: {templateComments.length} of{" "}
                            {templateComments.length}
                          </p>
                        </div>
                      )}
                      <Field
                        name="fileUploads"
                        component={FileUploadForm}
                        documentData={getChangedDocumentsModuleData(documentType)}
                        resolvedFields={
                          document.resolvedFields ||
                          _get(document?.templateFile, "resolvedFields")
                        }
                        onlyPreview
                        showDocumentLabel
                        addReviewComment={!isViewMode}
                        hideResolveAllCheckbox={!isViewMode}
                        formName="approvalDocuments"
                        formHelperKey="ManagerCompanyDocument"
                        updateEffectKey={"updateTransactionUnderReview"}
                        updateEffectPath={`transactionSlice.transactionUnderReview.items.approvalDocs.${officialIndex}.${documentName}`}
                        formId={_get(document?.templateFile, "_id")}
                        resolveAllFieldsCheckbox
                        editorKey={document?.templateFile.fileName}
                        documentName={documentType}
                        comments={templateComments}
                        handleSaveComment={handleSaveComment}
                      />
                    </div>
                  )}
                </Formik>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default ApprovalDocument;
